import type {
  CreateTournamentRequest,
  PatchTournamentRequest,
  HeldTournament,
  TemplateField,
  TournamentSessionRequest,
} from '@brush/api-client';
import {
  columnFor,
  emptyRow,
  listPhrase,
  missingRequiredColumns,
  normaliseKey,
  SESSION_DAY_KEY,
  type ImportColumn,
  type RowDraft,
} from './columns.ts';
import { isBlankRow } from './csv.ts';
import { diffTournament, type FieldChange, type GovernedFields } from './diff.ts';

/**
 * Reading a parsed CSV against a room's template into tournaments it could create.
 *
 * Nothing here talks to the server. The plan is what the review step shows and what the import
 * step posts, and it is built the same way either time — so what an operator approves is exactly
 * what gets sent.
 *
 * Two file shapes come out of the same code path, decided by whether the template declares a
 * session day:
 *
 *  - **One row per tournament.** Internal IDs are unique, and a `multi_day` column saying yes
 *    with no sittings listed is the real "multi-day, schedule to be announced" state.
 *  - **One row per sitting.** Rows sharing an internal ID are days of one event. The first such
 *    row supplies the tournament's own fields — a Day 2 row carries no buy-in — and each row
 *    becomes a session.
 */

export type IssueLevel = 'error' | 'warning';

export interface Issue {
  level: IssueLevel;
  /** The line in the file, 1-based, counting the heading row — what a spreadsheet shows. */
  line?: number;
  message: string;
}

/** One field key from the room's content, resolved against the importer's vocabulary. */
export interface PlannedColumn {
  fieldKey: string;
  label: string;
  /** Null where the key isn't one the importer understands, so the column is ignored. */
  column: ImportColumn | null;
}

/**
 * What importing this tournament would do.
 *
 * A file re-run against a schedule it has already been imported into is ordinary, so an internal
 * ID the room already holds is not a collision — it is a question about whether anything moved.
 */
export type PlannedAction = 'create' | 'update' | 'unchanged';

/** A tournament the file describes: one row, or several sharing an internal ID. */
export interface PlannedTournament {
  internalId: string;
  name: string;
  date: string;
  /** The file lines it was read from. */
  lines: number[];
  sessionCount: number;
  issues: Issue[];
  action: PlannedAction;
  /** What differs from what the room already holds. Empty unless `action` is `update`. */
  changes: FieldChange[];
  /**
   * What the file says this tournament is — for every action, not just a create, because the
   * review shows the file's version of a row whatever is going to happen to it. Null only when
   * the row has errors and nothing could be built from it.
   */
  values: CreateTournamentRequest | null;
  /** The patch and its target — set only when `action` is `update` and this one has no errors. */
  update: PlannedUpdate | null;
}

export interface PlannedUpdate {
  id: string;
  internalId: string;
  /** Only the fields that differ. Nothing the file has no opinion about is written. */
  body: Partial<PatchTournamentRequest>;
}

export interface ImportPlan {
  columns: PlannedColumn[];
  /** Whether the first line was read as headings rather than data. */
  hasHeader: boolean;
  /** Whether rows sharing an internal ID are sittings of one event. */
  grouped: boolean;
  tournaments: PlannedTournament[];
  /** Everything wrong, file-level first and then in line order. */
  issues: Issue[];
  errorCount: number;
  warningCount: number;
  dataRows: number;
  blankRows: number;
  /** Tournaments the room already holds exactly as the file describes them. */
  unchangedCount: number;
  /**
   * Whether what the room already holds is still being read (known gap 112). Until it is, nothing is
   * planned — no create, no update — because a held tournament planned as a create is refused at the
   * run and an unchanged one would be reported as new. The file's own problems are still reported.
   */
  pending: boolean;
  /** What would be sent. Both empty while anything is wrong. */
  creates: CreateTournamentRequest[];
  updates: PlannedUpdate[];
}

export interface PlanInput {
  roomId: string;
  /** The template's fields, for naming a column the way the room sees it. */
  fields: readonly TemplateField[];
  /** The room's chosen columns, in its own order. */
  fieldKeys: readonly string[];
  rows: string[][];
  /**
   * The tournaments the room already holds of those the file names (`fileInternalIds`, known gap 108), for
   * telling a re-import from a new one and for working out what moved.
   *
   * **Null while they are still being read**, which is not an empty room: the plan then waits
   * (`pending`) rather than planning every tournament as new (known gap 112).
   */
  existing: readonly HeldTournament[] | null;
  /** Rooms cannot remove a platform tag, so an update never drops one the file omits. */
  platformTags: readonly string[];
}

/**
 * As many tournaments as one run will take on. The import is a create request per tournament,
 * so this is a bound on how long an operator sits watching a progress bar — and on how much a
 * mis-picked file can do before anyone notices.
 */
export const MAX_TOURNAMENTS = 500;

const error = (message: string, line?: number): Issue => ({ level: 'error', message, line });
const warning = (message: string, line?: number): Issue => ({ level: 'warning', message, line });

/** Case-insensitive, matching the SQL Server collation the server compares internal IDs under. */
const idKey = (internalId: string) => internalId.trim().toLowerCase();

/** A leading count in the units cell — "5 Seats" is a guarantee of 5, in seats. */
const COUNTED_UNITS = /^(\d+(?:\.\d+)?)\s+(.+)$/;

/** One line of the file, read against the template's columns. */
interface ReadRow {
  line: number;
  draft: RowDraft;
  issues: Issue[];
  /**
   * Columns whose cell said something that wouldn't parse. Already reported in their own words,
   * so the field is not also announced as missing — "that isn't a date" and "a date is required"
   * are one problem, and printing both reads as two.
   */
  failed: Set<string>;
}

/** A plan that does nothing, for the cases that fail before a row is ever read. */
const empty = (columns: PlannedColumn[], issues: Issue[]): ImportPlan => ({
  columns,
  hasHeader: false,
  grouped: false,
  tournaments: [],
  issues,
  errorCount: issues.filter((i) => i.level === 'error').length,
  warningCount: issues.filter((i) => i.level === 'warning').length,
  dataRows: 0,
  blankRows: 0,
  unchangedCount: 0,
  pending: false,
  creates: [],
  updates: [],
});

/** The room's chosen columns, each resolved against the importer's vocabulary and named as the room sees it. */
function plannedColumns(input: Pick<PlanInput, 'fields' | 'fieldKeys'>): PlannedColumn[] {
  const labels = new Map(input.fields.map((f) => [f.key, f.label]));
  return input.fieldKeys.map((fieldKey) => ({
    fieldKey,
    label: labels.get(fieldKey) ?? fieldKey,
    column: columnFor(fieldKey),
  }));
}

/**
 * Whether the first line is headings rather than data — one rule, shared by the plan and by the lookup of
 * the file's internal IDs, so the two cannot disagree about whether line 1 is a tournament.
 */
function readsHeading(columns: readonly PlannedColumn[], firstRow: readonly string[]): boolean {
  const headingSpellings = new Set(
    columns.flatMap((c) => [normaliseKey(c.fieldKey), normaliseKey(c.label)]),
  );
  const named = firstRow.filter((cell) => cell.trim() !== '');

  /*
   * A cell that names a column — either one this template declares or one the importer knows of
   * generally. The second half matters: a file carrying columns the template has yet to declare
   * is exactly the case worth catching, and a heading row recognised only by the template's own
   * list would be read as data instead, burying the mismatch under a row of parse errors.
   */
  const namesAColumn = (cell: string) =>
    headingSpellings.has(normaliseKey(cell)) || columnFor(cell) !== null;

  // Half rather than all, for the same reason. No real row of tournament data is half column
  // names, so there is no ambiguity to trade away here.
  return named.length > 0 && named.filter(namesAColumn).length * 2 >= named.length;
}

/**
 * The internal IDs a file names, as the plan reads them: past a heading row, trimmed, once each whatever
 * their case, a blank naming nothing (known gap 108). What the importer asks the room about, so the held
 * tournaments it compares against are exactly those the file can match — no more read, none missed.
 */
export function fileInternalIds(input: Pick<PlanInput, 'fields' | 'fieldKeys' | 'rows'>): string[] {
  const columns = plannedColumns(input);
  const at = columns.findIndex((c) => c.column?.key === 'internal_id');
  if (at < 0 || input.rows.length === 0) return [];

  const seen = new Map<string, string>();
  for (let index = readsHeading(columns, input.rows[0]) ? 1 : 0; index < input.rows.length; index += 1) {
    const id = (input.rows[index][at] ?? '').trim();
    if (id !== '' && !seen.has(idKey(id))) seen.set(idKey(id), id);
  }
  return [...seen.values()];
}

export function buildPlan(input: PlanInput): ImportPlan {
  const columns = plannedColumns(input);

  const fileIssues: Issue[] = [];

  // A column the importer has no use for is reported once and then ignored — the room may well
  // carry it for its own reasons, and dropping it silently would look like data loss.
  for (const planned of columns) {
    if (!planned.column) {
      fileIssues.push(
        warning(
          `Column “${planned.label}” isn't one the importer recognises, so its values are ignored.`,
        ),
      );
    }
  }

  const missing = missingRequiredColumns(input.fieldKeys);
  if (missing.length > 0) {
    fileIssues.push(
      error(
        `This template doesn't include ${listPhrase(missing.map((m) => m.label))}. ` +
          `A tournament can't be created without ${missing.length === 1 ? 'it' : 'them'}.`,
      ),
    );
    return empty(columns, fileIssues);
  }

  if (input.rows.length === 0) {
    fileIssues.push(error('This file has no rows.'));
    return empty(columns, fileIssues);
  }

  // ---- heading row ----

  const firstRow = input.rows[0];
  const hasHeader = readsHeading(columns, firstRow);

  if (hasHeader) {
    // The template is what says which column is which, so a heading row that disagrees with it
    // would be read positionally into the wrong fields — a name landing in the date column
    // errors, but an internal ID landing in the name column would import quietly and wrongly.
    if (firstRow.length !== columns.length) {
      fileIssues.push(
        error(
          `The heading row has ${firstRow.length} columns but this template declares ` +
            `${columns.length}. Update the template's columns, or the file, so the two agree.`,
        ),
      );
    } else {
      for (let i = 0; i < columns.length; i += 1) {
        const heading = firstRow[i];
        const planned = columns[i];
        const matches =
          normaliseKey(heading) === normaliseKey(planned.fieldKey) ||
          normaliseKey(heading) === normaliseKey(planned.label);
        if (!matches) {
          fileIssues.push(
            error(
              `Column ${i + 1} is headed “${heading.trim()}” but this template expects ` +
                `“${planned.label}”.`,
            ),
          );
        }
      }
    }
  }

  if (fileIssues.some((i) => i.level === 'error')) {
    return { ...empty(columns, fileIssues), hasHeader };
  }

  // ---- read every data row ----

  const grouped = input.fieldKeys.some((key) => columnFor(key)?.key === SESSION_DAY_KEY);

  const readRows: ReadRow[] = [];
  let blankRows = 0;

  for (let index = hasHeader ? 1 : 0; index < input.rows.length; index += 1) {
    const cells = input.rows[index];
    const line = index + 1;

    if (isBlankRow(cells)) {
      blankRows += 1;
      continue;
    }

    const issues: Issue[] = [];

    if (cells.length > columns.length) {
      issues.push(
        warning(
          `This row has ${cells.length} values but the template declares ${columns.length} ` +
            `columns — the extra ones are ignored.`,
          line,
        ),
      );
    } else if (cells.length < columns.length) {
      issues.push(
        warning(
          `This row has ${cells.length} values but the template declares ${columns.length} ` +
            `columns — the rest are read as blank.`,
          line,
        ),
      );
    }

    const draft = emptyRow();
    const failed = new Set<string>();
    for (let i = 0; i < columns.length; i += 1) {
      const column = columns[i].column;
      if (!column) continue;
      const message = column.read(cells[i] ?? '', draft);
      if (message) {
        issues.push(error(`${columns[i].label}: ${message}`, line));
        failed.add(column.key);
      }
    }

    readRows.push({ line, draft, issues, failed });
  }

  // ---- group into tournaments ----

  const order: string[] = [];
  const groups = new Map<string, ReadRow[]>();

  for (const row of readRows) {
    const internalId = row.draft.internalId;
    if (!internalId) {
      fileIssues.push(error('An internal ID is required.', row.line));
      continue;
    }

    const key = idKey(internalId);
    const existing = groups.get(key);
    if (!existing) {
      order.push(key);
      groups.set(key, [row]);
      continue;
    }

    // Without a session day there is nothing to make a second row mean, so a repeat is two
    // tournaments claiming one reference — which the server refuses too.
    if (!grouped) {
      row.issues.push(
        error(
          `Internal ID “${internalId}” is already used on line ${existing[0].line}.`,
          row.line,
        ),
      );
    }
    existing.push(row);
  }

  const held = new Map((input.existing ?? []).map((t) => [idKey(t.internalId), t]));

  // What the template has an opinion about. A column it doesn't declare is neither compared
  // against what the room holds nor written by an update.
  const declares = (key: string) => columns.some((c) => c.column?.key === key);
  const governs: GovernedFields = {
    name: declares('name'),
    guarantee: declares('guarantee') || declares('has_guarantee'),
    guaranteeUnits: declares('guarantee_units'),
    eventNumber: declares('event_number'),
    status: declares('status'),
    tags: declares('tags'),
    schedule: declares(SESSION_DAY_KEY) || declares('is_multi_day'),
  };

  const tournaments: PlannedTournament[] = order.map((key) =>
    reconcile(groups.get(key) as ReadRow[], {
      roomId: input.roomId,
      grouped,
      held,
      governs,
      platformTags: input.platformTags,
    }),
  );

  if (tournaments.length > MAX_TOURNAMENTS) {
    fileIssues.push(
      error(
        `This file describes ${tournaments.length} tournaments. The importer takes at most ` +
          `${MAX_TOURNAMENTS} at a time — split the file and run it again.`,
      ),
    );
  }

  const issues = [
    ...fileIssues.filter((i) => i.line === undefined),
    ...[...fileIssues.filter((i) => i.line !== undefined), ...tournaments.flatMap((t) => t.issues)]
      .slice()
      .sort((a, b) => (a.line ?? 0) - (b.line ?? 0)),
  ];

  const errorCount = issues.filter((i) => i.level === 'error').length;

  // All or nothing: a plan with an error in it has not been approved, and half a schedule is
  // worse than none — the operator can't tell by looking which half arrived. A plan still waiting
  // on what the room holds has not been decided at all.
  const pending = input.existing === null;
  const blocked = errorCount > 0 || pending;

  return {
    columns,
    hasHeader,
    grouped,
    tournaments,
    issues,
    errorCount,
    warningCount: issues.filter((i) => i.level === 'warning').length,
    dataRows: readRows.length,
    blankRows,
    unchangedCount: pending ? 0 : tournaments.filter((t) => t.action === 'unchanged').length,
    pending,
    creates: blocked
      ? []
      : tournaments
          .filter((t) => t.action === 'create')
          .map((t) => t.values)
          .filter((r): r is CreateTournamentRequest => r !== null),
    updates: blocked
      ? []
      : tournaments.map((t) => t.update).filter((u): u is PlannedUpdate => u !== null),
  };
}

interface ReconcileContext {
  roomId: string;
  grouped: boolean;
  /** The room's existing tournaments, keyed by internal ID. */
  held: ReadonlyMap<string, HeldTournament>;
  governs: GovernedFields;
  platformTags: readonly string[];
}

/**
 * One internal ID's rows into one tournament.
 *
 * The first row is authoritative for everything about the event itself. Later rows are sittings,
 * and their copies of the name, buy-in and the rest are ignored — a Day 2 row in a real schedule
 * leaves the buy-in blank, so reading them would mean deciding which blank wins.
 */
function reconcile(rows: ReadRow[], context: ReconcileContext): PlannedTournament {
  const lead = rows[0];
  const draft = lead.draft;
  const lines = rows.map((r) => r.line);
  const issues: Issue[] = rows.flatMap((r) => r.issues);
  const internalId = draft.internalId ?? '';

  const require = <T,>(value: T | null, label: string, key: string): T | null => {
    if (value === null && !lead.failed.has(key)) {
      issues.push(error(`${label} is required.`, lead.line));
    }
    return value;
  };

  const name = require(draft.name, 'A tournament name', 'name');
  const date = require(draft.date, 'A date', 'date');
  const time = require(draft.time, 'A start time', 'time');
  const buyIn = require(draft.buyIn, 'A buy-in', 'buy_in');

  const held = context.held.get(idKey(internalId));

  // ---- guarantee ----

  let guarantee = draft.guarantee;
  let guaranteeUnits = draft.guaranteeUnits;

  // "5 Seats" in the units column with no amount beside it is a guarantee of five seats. The
  // contract splits the two, so the file's one cell has to.
  if (guarantee === null && guaranteeUnits) {
    const counted = COUNTED_UNITS.exec(guaranteeUnits);
    if (counted) {
      guarantee = Number(counted[1]);
      guaranteeUnits = counted[2].trim() || null;
    }
  }

  if (draft.hasGuarantee === true && guarantee === null) {
    issues.push(
      error('This says a guarantee was advertised but doesn’t say how much.', lead.line),
    );
  }
  if (draft.hasGuarantee === false && guarantee !== null) {
    // Nothing guaranteed is not a guarantee of zero, and it is not a guarantee of $20,000
    // either. Picking one of two contradicting cells would publish a figure nobody wrote.
    issues.push(
      error(
        `This says nothing was guaranteed but gives an amount of ${guarantee}. ` +
          'One of the two is wrong.',
        lead.line,
      ),
    );
  }
  if (draft.hasGuarantee === false) {
    guarantee = null;
    guaranteeUnits = null;
  }

  // ---- sittings ----

  const sessions: TournamentSessionRequest[] = [];
  let isMultiDay = draft.isMultiDay ?? false;

  if (context.grouped) {
    if (rows.length > 1) {
      if (draft.isMultiDay === false) {
        issues.push(
          error(
            `Line ${lead.line} says this isn't multi-day, but ${rows.length} sittings are ` +
              'listed for it.',
            lead.line,
          ),
        );
      }
      isMultiDay = true;
    }

    if (isMultiDay) {
      const seen = new Map<string, number>();

      for (const row of rows) {
        const day = row.draft.sessionDay;
        if (day === null) {
          issues.push(error('A session day is required for each sitting.', row.line));
          continue;
        }
        if (row.draft.date === null || row.draft.time === null) {
          // Already reported by `require` for the lead row; a later row needs saying here.
          if (row !== lead) {
            issues.push(error('A sitting needs a date and a start time.', row.line));
          }
          continue;
        }

        // Day 1A names one sitting — the server rejects a repeat, and so should the review.
        const key = `${day}|${(row.draft.sessionName ?? '').toUpperCase()}`;
        const first = seen.get(key);
        if (first !== undefined) {
          issues.push(
            error(
              `Day ${day}${row.draft.sessionName ?? ''} is already listed on line ${first}.`,
              row.line,
            ),
          );
          continue;
        }
        seen.set(key, row.line);

        sessions.push({
          day,
          sessionName: row.draft.sessionName,
          date: row.draft.date,
          time: row.draft.time,
          // The row's own wording where it has one; the server falls back to the parent's name.
          name: row.draft.name,
        });
      }
    }
  }

  const ok = issues.every((i) => i.level !== 'error');
  const complete = name !== null && date !== null && time !== null && buyIn !== null;

  const request: CreateTournamentRequest | null =
    ok && complete
      ? {
          roomId: context.roomId,
          internalId,
          name,
          date,
          time,
          buyIn,
          guarantee,
          guaranteeUnits,
          eventNumber: draft.eventNumber,
          isMultiDay,
          // A multi-day event with nothing listed is "schedule to be announced", which the
          // contract carries as the flag alone.
          sessions: sessions.length > 0 ? sessions : null,
          status: draft.status,
          tags: draft.tags,
        }
      : null;

  const base = {
    internalId,
    name: name ?? internalId,
    date: date ?? '',
    lines,
    sessionCount: sessions.length,
    issues,
  };

  // Nothing to compare against: this is a tournament the room doesn't hold.
  if (!held) {
    return { ...base, action: 'create' as const, changes: [], values: request, update: null };
  }

  // It exists, and the row is unusable, so there is nothing to compare either. The errors already
  // on it are what the operator needs to see.
  if (!request) {
    return { ...base, action: 'update' as const, changes: [], values: null, update: null };
  }

  const { changes, patch } = diffTournament({
    existing: held,
    next: request,
    governs: context.governs,
    platformTags: context.platformTags,
  });

  if (changes.length === 0) {
    // A re-import of a file nothing has moved in. Reported as a count rather than a per-row
    // notice: on a re-run this is most of the file, and a warning each would bury the few that
    // did move.
    return { ...base, action: 'unchanged' as const, changes: [], values: request, update: null };
  }

  /*
   * Not pushed onto `issues`.
   *
   * A tournament that moved is reported against its own row, where the before and after sit next
   * to the values they are about. Duplicating it into the issue list would put the same thing in
   * two scrolling panels and make "warnings" a number that counts updates twice — and on a
   * re-import of a schedule that shifted, that list is the whole file.
   */
  return {
    ...base,
    action: 'update' as const,
    changes,
    values: request,
    update: { id: held.id, internalId, body: patch },
  };
}
